import ky, { HTTPError, type KyInstance } from "ky";

const defaultApiBase =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.PROD ? "https://hub.rin.ci" : "http://localhost:8080");

export type UserRole = "admin" | "manager" | "user" | "service";
export type AuthMethod = "service_key" | "api_key" | "sso_header";
export type DashboardScope = "mine" | "all";

export type AuthUser = {
  id: string;
  email: string;
  name?: string | null;
  role: UserRole;
};

export type AuthSession = {
  token: string;
  user: AuthUser;
  scopes: string[];
};

export type AuthResponse = {
  user: AuthUser;
  auth: {
    method: AuthMethod;
    provider: "internal" | "sso" | "oauth";
    providerId?: string | null;
    scopes: string[];
  };
};

export type ProviderStatus = {
  name: "smtp" | "zoho" | "resend";
  order: number;
  role: "primary" | "fallback" | "disabled";
  configured: boolean;
  host?: string;
  endpoint?: string;
  accounts?: string[];
};

export type ProvidersResponse = {
  order: ProviderStatus["name"][];
  primary?: ProviderStatus["name"];
  fallback?: ProviderStatus["name"];
  providers: ProviderStatus[];
};

export type ProvidersConfigInput = {
  order?: ProviderStatus["name"][];
  enabled?: Partial<Record<ProviderStatus["name"], boolean>>;
};

export type UsageEvent = {
  id: string;
  userId: string | null;
  apiKeyId: string | null;
  authMethod: AuthMethod | null;
  requestId: string;
  method: string;
  path: string;
  statusCode: number;
  durationMs: number;
  userAgent: string | null;
  ip: string | null;
  errorCode: string | null;
  createdAt: string;
};

export type DashboardSummary = {
  totals: {
    requests: number;
    requests24h: number;
    errors24h: number;
    emails: number;
    emails24h: number;
    failedEmails24h: number;
  };
  recentUsage: UsageEvent[];
};

export type DashboardAnalytics = {
  range: {
    since: string;
    until: string;
  };
  totals: {
    requests: number;
    errors: number;
    emails: number;
    failedEmails: number;
    activeUsers: number;
    averageDurationMs: number;
    failureRate: number;
  };
  requestsByStatus: Array<{ name: string; count: number }>;
  emailsByStatus: Array<{ name: string; count: number }>;
  emailsByProvider: Array<{ name: string; count: number }>;
  topRoutes: Array<{
    path: string;
    method: string;
    requests: number;
    errors: number;
    averageDurationMs: number;
  }>;
};

export type EmailEvent = {
  id: string;
  userId: string | null;
  apiKeyId: string | null;
  requestId: string | null;
  jobId: string | null;
  to: string;
  subject: string;
  status: "queued" | "processing" | "sent" | "failed";
  provider: ProviderStatus["name"] | null;
  messageId: string | null;
  error: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
};

export type AppLog = {
  id: string;
  level: "debug" | "info" | "warn" | "error";
  message: string;
  requestId: string | null;
  userId: string | null;
  path: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
};

export type DashboardUser = {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
  status: "active" | "disabled";
  apiKeyCount: number;
  requestCount: number;
  emailCount: number;
  lastSeenAt: string | null;
  createdAt: string;
};

export type DashboardApiKey = {
  id: string;
  userId: string;
  userEmail: string;
  userName: string | null;
  userRole: UserRole;
  name: string;
  keyPrefix: string;
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
};

export type RegisterUserInput = {
  email: string;
  name?: string;
  scopes?: string[];
};

export type RegisterUserResponse = AuthResponse & {
  apiKey: {
    id: string;
    name: string;
    keyPrefix: string;
    token: string;
    scopes: string[];
    expiresAt: string | null;
    createdAt: string;
  };
};

export type RenewApiKeyResponse = {
  id: string;
  name: string;
  keyPrefix: string;
  token: string;
  scopes: string[];
  expiresAt: string | null;
  createdAt: string;
  revokedKeyId: string;
};

export type RevokeApiKeyResponse = {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  expiresAt: string | null;
  createdAt: string;
  revokedAt: string;
};

export type SendEmailInput = {
  from?: string;
  to: string | string[];
  subject: string;
  text?: string;
  html?: string;
  tags?: Array<{ name: string; value: string }>;
};

export class ApiClient {
  private readonly client: KyInstance;

  constructor(
    apiBase = defaultApiBase,
    private readonly token?: string,
  ) {
    this.client = ky.create({
      prefix: `${apiBase.replace(/\/$/, "")}/api`,
      headers: token ? { authorization: `Bearer ${token}` } : undefined,
      timeout: 20_000,
    });
  }

  withToken(token: string) {
    return new ApiClient(defaultApiBase, token);
  }

  private async json<T>(request: Promise<Response>): Promise<T> {
    try {
      const response = await request;
      return (await response.json()) as T;
    } catch (error) {
      if (error instanceof HTTPError) {
        const body = await error.response
          .json<{
            error?: { message?: string };
            message?: string;
          }>()
          .catch(() => null);
        throw new Error(
          body?.error?.message ??
            body?.message ??
            `Request failed with status ${error.response.status}`,
        );
      }
      throw error;
    }
  }

  login(token: string) {
    const client = new ApiClient(defaultApiBase, token);
    return client.me();
  }

  me() {
    return this.json<AuthResponse>(this.client.get("auth/me"));
  }

  dashboard(scope: DashboardScope = "mine") {
    return this.json<DashboardSummary>(
      this.client.get("dashboard", { searchParams: { scope, limit: "50" } }),
    );
  }

  analytics(scope: DashboardScope = "mine") {
    return this.json<DashboardAnalytics>(
      this.client.get("dashboard/analytics", {
        searchParams: { scope, limit: "50" },
      }),
    );
  }

  providers() {
    return this.json<ProvidersResponse>(this.client.get("emails/providers"));
  }

  updateProviders(input: ProvidersConfigInput) {
    return this.json<ProvidersResponse>(
      this.client.put("emails/providers", { json: input }),
    );
  }

  registerUser(input: RegisterUserInput) {
    return this.json<RegisterUserResponse>(
      this.client.post("auth/register", { json: input }),
    );
  }

  users() {
    return this.json<{ users: DashboardUser[] }>(
      this.client.get("dashboard/users", { searchParams: { limit: "100" } }),
    );
  }

  apiKeys() {
    return this.json<{ apiKeys: DashboardApiKey[] }>(
      this.client.get("dashboard/api-keys", { searchParams: { limit: "200" } }),
    );
  }

  revokeApiKey(id: string) {
    return this.json<RevokeApiKeyResponse>(
      this.client.delete(`auth/api-keys/${id}`),
    );
  }

  renewApiKey(id: string) {
    return this.json<RenewApiKeyResponse>(
      this.client.post(`auth/api-keys/${id}/renew`),
    );
  }

  usage(scope: DashboardScope = "mine", limit = 50) {
    return this.json<{ usage: UsageEvent[] }>(
      this.client.get("dashboard/usage", {
        searchParams: { scope, limit: String(limit) },
      }),
    );
  }

  emailEvents(scope: DashboardScope = "mine", limit = 50) {
    return this.json<{ emailEvents: EmailEvent[] }>(
      this.client.get("dashboard/email-events", {
        searchParams: { scope, limit: String(limit) },
      }),
    );
  }

  logs(scope: DashboardScope = "mine", limit = 50) {
    return this.json<{ logs: AppLog[] }>(
      this.client.get("dashboard/logs", {
        searchParams: { scope, limit: String(limit) },
      }),
    );
  }

  sendEmail(input: SendEmailInput) {
    return this.json<{ id: string; queued: true }>(
      this.client.post("emails", { json: input }),
    );
  }

  resendEmailEvent(id: string) {
    return this.json<{ queued: true; sourceEventId: string }>(
      this.client.post(`dashboard/email-events/${id}/resend`),
    );
  }
}

export const api = new ApiClient();
